// CreateWorkspaceModal — Radix Dialog for creating a new workspace.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useAppSelector } from '~/hooks/useAppSelector';
import {
  createWorkspaceThunk,
  selectCreateWorkspaceInProgress,
  selectCreateWorkspaceError,
} from '../duck/workspaceDuck';
import translations from '../translations/en.json';
import Button from '~/common/components/Button';

interface CreateWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CreateWorkspaceModal({ open, onOpenChange }: CreateWorkspaceModalProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const inProgress = useAppSelector(selectCreateWorkspaceInProgress);
  const error = useAppSelector(selectCreateWorkspaceError);

  const [name, setName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const result = await dispatch(createWorkspaceThunk({ name: name.trim() }));
    if (createWorkspaceThunk.fulfilled.match(result)) {
      setName('');
      onOpenChange(false);
      navigate(`/workspaces/${result.payload.id}/boards`);
    }
  };

  const handleCancel = () => {
    setName('');
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-border bg-bg-base p-6 shadow-2xl focus:outline-none"
          aria-describedby="create-workspace-description"
        >
          <Dialog.Title className="mb-1 text-lg font-bold text-base">
            {translations['CreateWorkspaceModal.title']}
          </Dialog.Title>
          <p id="create-workspace-description" className="mb-5 text-sm text-muted">
            {translations['CreateWorkspaceModal.description']}
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label htmlFor="workspace-name" className="mb-1.5 block text-sm font-medium text-subtle">
                {translations['CreateWorkspaceModal.nameLabel']}
              </label>
              <input
                id="workspace-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={translations['CreateWorkspaceModal.namePlaceholder']}
                required
                autoFocus
                className="w-full rounded-lg border border-border bg-bg-overlay px-3 py-2 text-sm text-base placeholder:text-subtle focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {error && (
              <p className="mb-3 text-sm text-danger">
                {translations['CreateWorkspaceModal.errorGeneric']}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="md" onClick={handleCancel}>
                {translations['CreateWorkspaceModal.cancelButton']}
              </Button>
              <Button type="submit" variant="primary" size="md" disabled={inProgress || !name.trim()}>
                {inProgress
                  ? translations['CreateWorkspaceModal.submitting']
                  : translations['CreateWorkspaceModal.submitButton']}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
